import { create } from "zustand";

import { suppressDevAutoLogin } from "@/shared/api/auth.js";
import { apiState, setApiToken } from "@/shared/api/client.js";
import { fetchMe, type MeUser } from "@/shared/api/me.js";
import type { AuthSession } from "@/shared/api/session.js";

export type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

type AuthState = {
  token: string | null;
  currentUser: MeUser | null;
  status: AuthStatus;
  error: string | null;
  setToken: (token: string | null) => void;
  setSession: (session: AuthSession) => void;
  hydrate: (signal?: AbortSignal) => Promise<MeUser | null>;
  logout: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  token: apiState.token,
  currentUser: null,
  status: apiState.token ? "idle" : "unauthenticated",
  error: null,

  setToken(token) {
    setApiToken(token);
    set({
      token,
      currentUser: null,
      status: token ? "idle" : "unauthenticated",
      error: null,
    });
  },

  setSession(session) {
    setApiToken(session.token);
    set({
      token: session.token,
      currentUser: session.user,
      status: "authenticated",
      error: null,
    });
  },

  async hydrate(signal) {
    if (!apiState.token) {
      set({ token: null, currentUser: null, status: "unauthenticated", error: null });
      return null;
    }

    set({ token: apiState.token, status: "loading", error: null });
    try {
      const user = await fetchMe(signal);
      if (signal?.aborted) return null;
      set({
        token: apiState.token,
        currentUser: user,
        status: user ? "authenticated" : "unauthenticated",
        error: null,
      });
      return user;
    } catch (err) {
      if (signal?.aborted) return null;
      const message = err instanceof Error ? err.message : "这里出了点小问题，刷新看看？";
      set({ currentUser: null, status: "error", error: message });
      return null;
    }
  },

  logout() {
    suppressDevAutoLogin();
    setApiToken(null);
    set({ token: null, currentUser: null, status: "unauthenticated", error: null });
  },
}));
